import { useState } from 'react';
import { useDebounce } from 'common/useDebounce';
import { useTranslation } from 'react-i18next';
import { SetStateAction, useAtom, WritableAtom } from 'jotai';
import { SignleAutocomplete } from 'src/common/components/Inputs/Autocomplete/SignleAutocomplete';
import { Option } from 'src/common/components/Inputs/Autocomplete/utils/models.ts';
import { useAccountAutocomplete } from './hooks/useAccountAutocomplete';

type AccountSearchFilterProps = {
  accountsFilterAtom: WritableAtom<string | null, [SetStateAction<string | null>], void>;
  accountsHintsAtom: WritableAtom<string[], [any], void>;
  onAccountSelected?: (option: Option | null) => void;
  disabled?: boolean;
};

export const AccountSingleSearchFilter = ({
  accountsFilterAtom,
  accountsHintsAtom,
  onAccountSelected,
  ...props
}: AccountSearchFilterProps) => {
  const { t } = useTranslation();

  const [hints, removeHints] = useAtom(accountsHintsAtom);

  const [accountSearch, setAccountSearch] = useState('');
  const debounceAccount = useDebounce(accountSearch, 500);

  const [account, setAccount] = useAtom(accountsFilterAtom);

  const {
    options: accountsOptions,
    isSelectedOptionFetching,
    isFetched,
    selectedOption,
    hintOptions,
    updateSelectedOption,
    hasNextPage,
    fetchNextPage,
  } = useAccountAutocomplete({
    search: debounceAccount,
    initialId: account,
    hints: hints,
    onSelectedChange: (option) => {
      setAccount(option?.id || null);
      setAccountSearch('');
      if (onAccountSelected) {
        onAccountSelected(option);
      }
    },
  });

  const handleUpdateSelectedOptions = (option: Option | null) => {
    updateSelectedOption(option);
  };

  return (
    <SignleAutocomplete
      data-testid="accounts-select"
      placeholder={t('tariffSummary.filters.organisationPlaceholder')}
      label={t('tariffSummary.filters.organisation')}
      showNameAndId
      inputLoading={isSelectedOptionFetching}
      options={accountsOptions}
      value={selectedOption}
      hints={hintOptions}
      onChange={handleUpdateSelectedOptions}
      onSearch={setAccountSearch}
      loading={!isFetched}
      onHintRemove={removeHints}
      hasMore={hasNextPage}
      onMoreShouldBeLoad={fetchNextPage}
      {...props}
    />
  );
};
